import { useLiveQuery } from 'dexie-react-hooks';
import { KeyRound, UserRound } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { onlineManager } from '@tanstack/react-query';
import type { User } from '@doorly/shared';
import { db, type CachedUserRow } from '../../db/schema';
import { DialogHeader } from '../../components/ui/DialogHeader';
import { getActingAsUserId, getActingDeviceUserId, clearActingAs } from '../../lib/actingAs';
import { useApiQuery } from '../../lib/api';
import { cn } from '../../lib/cn';
import { Avatar, initialsFrom } from '../../components/ui/avatar';
import { Button } from '../../components/ui/button';
import { toast } from '../../components/Toaster';
import { SyncEngine } from '../sync/SyncEngine';
import { PinPad } from './PinPad';
import { usePinSwitch, type PinSwitchFailure, type SwitchTarget } from './usePinSwitch';

interface SwitchUserModalProps {
  open: boolean;
  onClose: () => void;
}

function describeFailure(f: PinSwitchFailure): string {
  switch (f.kind) {
    case 'invalid':
      return f.remaining > 0
        ? `Code incorrect — ${f.remaining} essai${f.remaining > 1 ? 's' : ''} restant${f.remaining > 1 ? 's' : ''}.`
        : 'Code incorrect.';
    case 'locked': {
      const min = Math.max(1, Math.ceil(f.retryAfterMs / 60_000));
      return `Trop d'essais. Réessayez dans ${min} min, ou connectez-vous avec votre mot de passe.`;
    }
    case 'not_set':
      return "Ce vendeur n'a pas encore de code PIN. Il doit le poser depuis son compte (mot de passe).";
    case 'unavailable_offline':
      return 'Code indisponible hors ligne : la tablette doit se reconnecter une fois pour ce vendeur.';
    case 'error':
      return f.message;
  }
}

/**
 * « Changer d'utilisateur » — le parcours de la spec §4.1 : la liste des
 * vendeurs **du magasin**, un clic sur son nom, quatre chiffres, bascule.
 *
 * L'identité vient du clic, pas du code : deux vendeurs peuvent avoir le
 * même PIN sans collision, et le brute-force n'a de sens que sur une cible
 * déjà désignée — ce qui rend le compteur d'échecs simple à raisonner.
 *
 * La liste vient de Dexie (`db.users`, hydratée par le CacheWarmer au niveau
 * tablette) : elle fonctionne hors ligne, comme la bascule elle-même.
 */
export function SwitchUserModal({ open, onClose }: SwitchUserModalProps) {
  const me = useApiQuery<User>(['me'], '/users/me');
  // La tablette appartient au superviseur — ou au manager (AMS) : soit c'est lui qui est connecté
  // (première bascule), soit on est déjà en session PIN et il est mémorisé.
  // L'API d'abord (`deviceUserId` en session PIN, ou le superviseur lui-même),
  // le flag local seulement tant que `/users/me` n'a pas répondu.
  const deviceUserId = me.data
    ? (me.data.deviceUserId ??
      (me.data.role === 'superviseur' || me.data.role === 'manager' ? me.data.id : null))
    : getActingDeviceUserId();
  const currentId = getActingAsUserId();

  const vendeurs = useLiveQuery(
    async () => {
      if (!deviceUserId) return [] as CachedUserRow[];
      const rows = await db.users.where('role').equals('vendeur').toArray();
      return rows
        // Vendeurs directs de l'appareil : `superviseurId` d'un superviseur,
        // `managerId` d'un manager — un manager n'est jamais le superviseur
        // de personne (et inversement), le test tient sans connaître le rôle,
        // hors ligne compris. Même règle que l'API (`pin-team.ts`).
        .filter((u) => u.superviseurId === deviceUserId || u.managerId === deviceUserId)
        .sort((a, b) => a.lastName.localeCompare(b.lastName, 'fr'));
    },
    [deviceUserId],
    [] as CachedUserRow[],
  );

  const [target, setTarget] = useState<SwitchTarget | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [shake, setShake] = useState(0);
  const { switchTo, busy } = usePinSwitch(deviceUserId);

  useEffect(() => {
    if (!open) {
      setTarget(null);
      setError(null);
    }
  }, [open]);

  const onPin = useCallback(
    async (pin: string) => {
      if (!target) return;
      setError(null);
      const failure = await switchTo(target, pin);
      if (failure) {
        setError(describeFailure(failure));
        setShake((n) => n + 1);
      }
    },
    [target, switchTo],
  );

  /**
   * « Mot de passe » (spec §4.2) : la session tablette est remplacée par une
   * connexion complète — le superviseur la rouvrira. Avant de lâcher la
   * session, la file hors ligne doit être vide : ses fiches partiraient
   * sinon sous un compte qui n'a pas le droit de les porter.
   */
  const [leaving, setLeaving] = useState(false);
  const goToPassword = useCallback(async () => {
    setLeaving(true);
    try {
      // Seules les FICHES comptent : une déclaration de bascule en attente ne
      // doit pas bloquer la sortie. Et `dead` compte — c'est précisément ce que
      // `/logout` détruirait sans retour.
      const pendingLeads = () =>
        db.mutations
          .where('status')
          .anyOf('queued', 'in_flight', 'failed', 'dead')
          .filter((m) => m.entity === 'lead')
          .count();
      const pending = await pendingLeads();
      if (pending > 0) {
        if (!onlineManager.isOnline()) {
          toast.error(
            `${pending} fiche${pending > 1 ? 's' : ''} en attente de synchronisation. ` +
              'Reconnectez la tablette avant de changer de mode de connexion.',
          );
          return;
        }
        await SyncEngine.forceFlush();
        for (let i = 0; i < 20; i++) {
          await new Promise((r) => setTimeout(r, 500));
          if ((await pendingLeads()) === 0) break;
        }
        const stuck = await pendingLeads();
        if (stuck > 0) {
          toast.error(
            `${stuck} fiche${stuck > 1 ? 's' : ''} n'a pas pu être synchronisée — réessayez dans un instant.`,
          );
          return;
        }
      }
      clearActingAs();
      window.location.href = '/logout?next=/connexion';
    } finally {
      setLeaving(false);
    }
  }, []);

  const title = useMemo(
    () => (target ? `${target.firstName} ${target.lastName}` : "Changer d'utilisateur"),
    [target],
  );

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-end md:items-center justify-center">
      <div className="absolute inset-0 bg-ink/40" onClick={busy ? undefined : onClose} />
      <div
        className="relative w-full md:max-w-md bg-surface flex flex-col max-h-[92dvh]"
        style={{
          borderRadius: 'var(--radius-card) var(--radius-card) 0 0',
          boxShadow: 'var(--shadow-elevated)',
        }}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <DialogHeader
          title={title}
          icon={UserRound}
          onClose={onClose}
          onBack={
            target
              ? () => {
                  setTarget(null);
                  setError(null);
                }
              : undefined
          }
          disabled={busy}
        />

        <div className="flex-1 overflow-y-auto p-4">
          {!target ? (
            <div className="space-y-3">
              {vendeurs.length === 0 ? (
                <p className="text-body text-ink-mute text-center py-8">
                  {deviceUserId
                    ? 'Aucun vendeur rattaché à ce magasin.'
                    : 'La tablette doit être ouverte par le superviseur ou le manager du magasin.'}
                </p>
              ) : (
                <ul className="grid grid-cols-2 gap-3">
                  {vendeurs.map((v) => {
                    const isCurrent = v.id === currentId;
                    const ready = v.hasPin === true;
                    return (
                      <li key={v.id}>
                        <button
                          type="button"
                          disabled={isCurrent || !ready}
                          onClick={() =>
                            setTarget({ id: v.id, firstName: v.firstName, lastName: v.lastName })
                          }
                          className={cn(
                            'relative w-full h-[124px] p-3 flex flex-col items-center justify-center gap-2 rounded-[var(--radius-card)] border text-center transition-colors',
                            isCurrent
                              ? 'border-brand bg-brand-soft/50'
                              : ready
                                ? 'border-hair bg-surface hover:border-brand active:bg-brand-soft/40'
                                : 'border-hair bg-surface-alt/60 opacity-70',
                          )}
                        >
                          <Avatar initials={initialsFrom(v.firstName, v.lastName)} size={44} />
                          <span className="text-body font-bold text-ink leading-tight">
                            {v.firstName} <span className="uppercase">{v.lastName}</span>
                          </span>
                          {/* Étiquette d'état hors du flux : elle n'a pas à décaler
                              l'avatar et le nom, qui restent centrés dans la carte. */}
                          {(isCurrent || !ready) && (
                            <span
                              className={cn(
                                'absolute bottom-2 inset-x-0 text-micro font-semibold',
                                isCurrent ? 'text-brand' : 'text-ink-mute',
                              )}
                            >
                              {isCurrent ? 'Connecté' : 'Sans code'}
                            </span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          ) : (
            <div className="py-2">
              <PinPad
                onComplete={onPin}
                disabled={busy}
                shakeKey={shake}
                label={busy ? 'Vérification…' : 'Saisissez votre code à 4 chiffres'}
              />
              <p
                className={cn(
                  'mt-4 min-h-[1.5rem] text-center text-meta',
                  error ? 'text-danger font-semibold' : 'text-transparent',
                )}
                role="alert"
              >
                {error ?? '·'}
              </p>
            </div>
          )}
        </div>

        <footer className="border-t border-hair p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          <Button
            type="button"
            variant="ghost"
            className="w-full text-ink-soft"
            onClick={() => void goToPassword()}
            disabled={busy || leaving}
          >
            <KeyRound className="h-4 w-4" />
            {leaving ? 'Synchronisation…' : 'Utiliser mon mot de passe'}
          </Button>
        </footer>
      </div>
    </div>
  );
}
